import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import Admin from "./Admin";
import AdminShell from "./admin/AdminShell";
import Overview from "./admin/Overview";
import Workspace from "./admin/Workspace";
import EngagementPage from "./EngagementPage";
import SignIn, { Loading, NoAccess, Card } from "./SignIn";
import { proxyCall } from "./proxy";
import { watchUser, signOut } from "./auth";
import { Session, useSession } from "./session";
import { T } from "./theme";

export default function App() {
  return (
    <BrowserRouter>
      <SessionGate>
        <HubRoutes />
      </SessionGate>
    </BrowserRouter>
  );
}

// Thread's two doors, behind one sign-in: the admin workspace (admins only)
// and the read-only client page.
export function HubRoutes() {
  return (
    <Routes>
      <Route path="/admin" element={<AdminOnly />}>
        <Route index element={<Overview />} />
        <Route path="engagements" element={<Admin />} />
        <Route path=":slug" element={<Workspace />} />
      </Route>
      <Route path="/client/:slug" element={<EngagementPage />} />
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function SessionGate({ children }) {
  const [user, setUser] = useState(undefined); // undefined while Firebase restores the session
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => watchUser(u => { setUser(u || null); setMe(null); setDenied(false); setFailed(false); }), []);

  useEffect(() => {
    if (!user) return;
    let live = true;
    proxyCall({ action: "me" })
      .then(m => { if (live) setMe(m); })
      .catch(e => { if (!live) return; if (e.status === 403) setDenied(true); else setFailed(true); });
    return () => { live = false; };
  }, [user]);

  const leave = async () => { await signOut(); };

  if (user === undefined) return <Loading />;
  if (!user) return <SignIn />;
  if (denied) return <NoAccess email={user.email} onSignOut={leave} />;
  if (failed) return (
    <Card title="Something went wrong">
      <p style={{ color: T.fgMuted, fontSize: 13, marginBottom: 16 }}>We couldn't load your access. Reload the page to try again.</p>
    </Card>
  );
  if (!me) return <Loading />;
  return <Session.Provider value={{ me, signOut: leave }}>{children}</Session.Provider>;
}

// After sign-in: a client goes straight to their engagement; an admin to the
// admin view. Nobody sees a list of other organisations.
export function Home() {
  const { me, signOut: leave } = useSession();
  if (me.role === "admin") return <Navigate to="/admin" replace />;
  if (me.engagements.length === 1) return <Navigate to={`/client/${me.engagements[0].slug}`} replace />;
  return (
    <Card title="Your engagements">
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {me.engagements.map(e => (
          <Link key={e.slug} to={`/client/${e.slug}`} style={{ padding: "12px 16px", border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, color: T.root, fontWeight: 600, fontSize: 14, textDecoration: "none" }}>{e.name}</Link>
        ))}
      </div>
      <button onClick={leave} style={{ fontSize: 12, color: T.fgMuted, background: "none", border: "none", cursor: "pointer", fontFamily: T.fontSans }}>Sign out</button>
    </Card>
  );
}

// The server refuses every admin action to a non-admin; this only keeps a
// client from landing on screens that would show nothing.
function AdminOnly() {
  const { me } = useSession();
  return me.role === "admin" ? <AdminShell /> : <Navigate to="/" replace />;
}
